import { ClassConstructor, classToPlain, plainToClass } from 'class-transformer';

import { defaultDecoder, parse, stringify } from 'qs';
import LoggerFactory from './Logger/LoggerFactory';

export function removeUndefinedProps<T>(obj: Partial<T>): Partial<T> {
  const o: { [x: string]: unknown } = obj;
  const keys = Object.keys(o);
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    if (o[key] === undefined) {
      delete o[key];
    }
  }
  return o as Partial<T>;
}

export function removeTrailingSlashes(url: string): string {
  return url.replace(/\/+$/, ''); // Removes one or more trailing slashes from URL
}

export function removeDuplicateForwardSlashes(url: string): string {
  return url.replace(/([^:]\/)\/+/g, '$1'); // remove duplicate forward slashes
}

// attention in js use encodeURI() for the url, but encodeURIComponent() for params!
// see https://stackoverflow.com/questions/75980/when-are-you-supposed-to-use-escape-instead-of-encodeuri-encodeuricomponent/3608791#3608791

export function encodeUrlParam(str: string): string {
  return encodeURIComponent(str);
}

export function decodeUrlParam(str: string): string {
  return decodeURIComponent(str);
}

export function encodeObjectasUrlParam(object: Partial<unknown>): string {
  // Attention can handle object depth of 5 max! You don't store objects in URLs are you?
  return stringify(classToPlain(object), {
    encode: false,
    skipNulls: true // plainToClass in decoder can handle this
  });
}

export function decodeObjectFromUrlParam<T>(t: ClassConstructor<T>, str: string): T {
  return plainToClass(
    t,
    parse(str, {
      allowDots: true,
      decoder(s: string, _d: defaultDecoder, charset: string) {
        const strWithoutPlus = s.replace(/\+/g, ' ');
        if (charset === 'iso-8859-1') {
          return strWithoutPlus.replace(/%[0-9a-f]{2}/gi, unescape);
        }

        if (/^(\d+|\d*\.\d+)$/.test(s)) {
          return parseFloat(s);
        }

        const keywords: Record<string, boolean | null | undefined> = {
          true: true,
          false: false,
          null: null,
          undefined
        };

        if (s in keywords) {
          return keywords[s];
        }

        // utf-8
        try {
          return decodeURIComponent(strWithoutPlus);
        } catch (e) {
          return strWithoutPlus;
        }
      }
    })
  );
}

export function decodeObjectasUrlParam<T>(t: ClassConstructor<T>, str: string): T {
  const logger = LoggerFactory.get('decodeObjectasUrlParam<T>');

  logger.warn('"decodeObjectasUrlParam" is deprecated - use "decodeObjectFromUrlParam" instead');
  return decodeObjectFromUrlParam<T>(t, str);
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function decodeAnyAsUrlParam(str: string): object {
  return JSON.parse(decodeURIComponent(str));
}
